import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { FaBluetoothB, FaCheckCircle, FaSyncAlt, FaLink, FaSearch } from "react-icons/fa";

const KNOWN_PRINTERS_KEY = "btKnownPrintersV1";
const CONNECTED_PRINTER_KEY = "printerConnected";
const CONNECTED_PRINTER_ID_KEY = "printerConnectedId";
const LAST_BLE_DEVICE_ID_KEY = "ble_last_device_id";
const BLE_OPTIONAL_SERVICES = [
  0xffe0,
  0xffe1,
  0xffe5,
  0x18f0,
  0x18f1,
  0xff00,
  0xff01,
  0xff02,
  "0000ffe0-0000-1000-8000-00805f9b34fb",
  "0000ffe1-0000-1000-8000-00805f9b34fb",
  "0000ff00-0000-1000-8000-00805f9b34fb",
  "0000ff01-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "000018f0-0000-1000-8000-00805f9b34fb",
  "000018f1-0000-1000-8000-00805f9b34fb",
];

const readKnownMeta = () => {
  try {
    const raw = localStorage.getItem(KNOWN_PRINTERS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const saveKnownMeta = (list) => {
  try {
    localStorage.setItem(KNOWN_PRINTERS_KEY, JSON.stringify(Array.isArray(list) ? list : []));
  } catch {
    // ignore
  }
};

const formatWhen = (iso) => {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function ConnectBluetoothPage() {
  const [discovered, setDiscovered] = useState([]);
  const [grantedDevices, setGrantedDevices] = useState([]);
  const [knownMeta, setKnownMeta] = useState(() => readKnownMeta());
  const [connectedDeviceId, setConnectedDeviceId] = useState(
    () =>
      localStorage.getItem(CONNECTED_PRINTER_ID_KEY) ||
      localStorage.getItem(LAST_BLE_DEVICE_ID_KEY) ||
      ""
  );
  const [connectedLabel, setConnectedLabel] = useState(() => localStorage.getItem(CONNECTED_PRINTER_KEY) || "");
  const [error, setError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [loadingKnown, setLoadingKnown] = useState(false);
  const [busyId, setBusyId] = useState("");

  const supportsBluetooth = typeof navigator !== "undefined" && Boolean(navigator.bluetooth);
  const supportsGetDevices = supportsBluetooth && typeof navigator.bluetooth.getDevices === "function";

  const mergeDevice = useCallback((list, device) => {
    if (!device?.id) return list;
    if (list.find((d) => d.id === device.id)) return list;
    return [...list, device];
  }, []);

  const upsertKnownPrinter = useCallback((device) => {
    const next = (() => {
      const now = new Date().toISOString();
      const entry = { id: device.id, name: device.name || "Unnamed Printer", lastConnectedAt: now };
      const rest = knownMeta.filter((item) => item.id !== device.id);
      return [entry, ...rest].slice(0, 20);
    })();
    setKnownMeta(next);
    saveKnownMeta(next);
  }, [knownMeta]);

  const loadGrantedDevices = useCallback(async () => {
    if (!supportsGetDevices) return;
    setLoadingKnown(true);
    setError("");
    try {
      const list = await navigator.bluetooth.getDevices();
      setGrantedDevices(Array.isArray(list) ? list : []);
      setDiscovered((prev) => (Array.isArray(list) ? list.reduce(mergeDevice, prev) : prev));
    } catch (e) {
      setError(`Unable to load previously allowed devices: ${e?.message || "Unknown error"}`);
    } finally {
      setLoadingKnown(false);
    }
  }, [mergeDevice, supportsGetDevices]);

  useEffect(() => {
    loadGrantedDevices();
  }, [loadGrantedDevices]);

  const connectDevice = useCallback(async (device) => {
    if (!device?.id) return;
    setBusyId(device.id);
    setError("");
    try {
      if (!device.gatt) {
        throw new Error("This device does not support GATT connection.");
      }
      if (!device.gatt.connected) {
        await device.gatt.connect();
      }
      // Validate that the granted device exposes a writable printer characteristic.
      const services = await device.gatt.getPrimaryServices();
      let writableFound = false;
      for (const svc of services) {
        const chars = await svc.getCharacteristics();
        for (const c of chars) {
          const { write, writeWithoutResponse } = c.properties || {};
          if (write || writeWithoutResponse) {
            writableFound = true;
            break;
          }
        }
        if (writableFound) break;
      }
      if (!writableFound) {
        throw new Error("No writable printer characteristic found for this device.");
      }
      const label = device.name || device.id;
      localStorage.setItem(CONNECTED_PRINTER_KEY, label);
      localStorage.setItem(CONNECTED_PRINTER_ID_KEY, device.id);
      localStorage.setItem(LAST_BLE_DEVICE_ID_KEY, device.id);
      setConnectedLabel(label);
      setConnectedDeviceId(device.id);
      upsertKnownPrinter(device);
    } catch (e) {
      setError(`Connection failed: ${e?.message || "Unknown error"}`);
    } finally {
      setBusyId("");
    }
  }, [upsertKnownPrinter]);

  const scanAndAddDevice = useCallback(async () => {
    if (!supportsBluetooth) return;
    setScanning(true);
    setError("");
    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: BLE_OPTIONAL_SERVICES,
      });
      setDiscovered((prev) => mergeDevice(prev, device));
      await connectDevice(device);
    } catch (e) {
      if (String(e?.name || "") !== "NotFoundError") {
        setError(`Scan failed: ${e?.message || "Unknown error"}`);
      }
    } finally {
      setScanning(false);
    }
  }, [connectDevice, mergeDevice, supportsBluetooth]);

  const reconnectFromKnown = useCallback(async (meta) => {
    let granted = grantedDevices.find((d) => d.id === meta.id);
    if (!granted && supportsGetDevices) {
      try {
        const refreshed = await navigator.bluetooth.getDevices();
        setGrantedDevices(Array.isArray(refreshed) ? refreshed : []);
        granted = (Array.isArray(refreshed) ? refreshed : []).find(
          (d) => d.id === meta.id
        );
      } catch {
        // ignore and fallback below
      }
    }
    if (granted) {
      await connectDevice(granted);
      return;
    }
    // Fallback for browsers where previous permission handle is unavailable.
    await scanAndAddDevice();
  }, [connectDevice, grantedDevices, scanAndAddDevice, supportsGetDevices]);

  useEffect(() => {
    if (!supportsGetDevices || !grantedDevices.length) return;
    const preferredId =
      localStorage.getItem(CONNECTED_PRINTER_ID_KEY) ||
      localStorage.getItem(LAST_BLE_DEVICE_ID_KEY) ||
      "";
    if (!preferredId) return;
    const preferred = grantedDevices.find((d) => d.id === preferredId);
    if (!preferred) return;
    if (connectedDeviceId === preferredId && preferred.gatt?.connected) return;
    connectDevice(preferred);
  }, [supportsGetDevices, grantedDevices, connectedDeviceId, connectDevice]);

  const preconnectedRows = useMemo(() => {
    const map = new Map();
    knownMeta.forEach((item) => map.set(item.id, { ...item, reconnectable: false }));
    grantedDevices.forEach((device) => {
      const current = map.get(device.id) || { id: device.id, name: device.name || "Unnamed Printer", lastConnectedAt: "" };
      map.set(device.id, {
        ...current,
        id: device.id,
        name: device.name || current.name || "Unnamed Printer",
        reconnectable: true,
      });
    });
    return Array.from(map.values());
  }, [grantedDevices, knownMeta]);

  if (!supportsBluetooth) {
    return (
      <Page>
        <Hero>
          <HeroTitle>Bluetooth Printer</HeroTitle>
          <HeroSub>
            This browser/device does not support Web Bluetooth. Use latest Chrome/Edge on desktop.
          </HeroSub>
        </Hero>
      </Page>
    );
  }

  return (
    <Page>
      <Hero>
        <HeroTitle>Bluetooth Printer Connection</HeroTitle>
        <HeroSub>Connect quickly and reuse previously paired printers in one click.</HeroSub>
        <TopActions>
          <PrimaryBtn type="button" onClick={scanAndAddDevice} disabled={scanning}>
            <FaSearch /> {scanning ? "Scanning..." : "Scan & Connect"}
          </PrimaryBtn>
          <GhostBtn type="button" onClick={loadGrantedDevices} disabled={loadingKnown}>
            <FaSyncAlt /> {loadingKnown ? "Refreshing..." : "Refresh Known Devices"}
          </GhostBtn>
        </TopActions>
        <StatusPill $ok={Boolean(connectedLabel)}>
          {connectedLabel ? (
            <>
              <FaCheckCircle /> Connected: {connectedLabel}
            </>
          ) : (
            <>
              <FaBluetoothB /> No printer connected
            </>
          )}
        </StatusPill>
      </Hero>

      {error ? <ErrorBox>{error}</ErrorBox> : null}

      <Section>
        <SectionHead>Previously Connected Devices</SectionHead>
        {preconnectedRows.length === 0 ? (
          <EmptyBox>No previous device found. Scan once to add your printer.</EmptyBox>
        ) : (
          <Grid>
            {preconnectedRows.map((item) => (
              <Card key={item.id}>
                <CardTop>
                  <DeviceName>{item.name || item.id}</DeviceName>
                  {connectedDeviceId === item.id ? <ConnectedTag>Live</ConnectedTag> : null}
                </CardTop>
                <MetaLine>ID: {item.id}</MetaLine>
                <MetaLine>Last connected: {formatWhen(item.lastConnectedAt)}</MetaLine>
                <RowActions>
                  <CardBtn
                    type="button"
                    onClick={() => reconnectFromKnown(item)}
                    disabled={busyId === item.id}
                  >
                    <FaLink />
                    {busyId === item.id ? "Connecting..." : item.reconnectable ? "Connect" : "Allow & Connect"}
                  </CardBtn>
                </RowActions>
              </Card>
            ))}
          </Grid>
        )}
      </Section>

      <Section>
        <SectionHead>Discovered In This Session</SectionHead>
        {discovered.length === 0 ? (
          <EmptyBox>No scanned devices yet.</EmptyBox>
        ) : (
          <Grid>
            {discovered.map((device) => (
              <Card key={device.id}>
                <CardTop>
                  <DeviceName>{device.name || device.id}</DeviceName>
                  {connectedDeviceId === device.id ? <ConnectedTag>Connected</ConnectedTag> : null}
                </CardTop>
                <MetaLine>ID: {device.id}</MetaLine>
                <RowActions>
                  <CardBtn
                    type="button"
                    onClick={() => connectDevice(device)}
                    disabled={busyId === device.id}
                  >
                    <FaLink /> {busyId === device.id ? "Connecting..." : "Connect"}
                  </CardBtn>
                </RowActions>
              </Card>
            ))}
          </Grid>
        )}
      </Section>
    </Page>
  );
}

const Page = styled.div`
  min-height: 100%;
  padding: 18px;
  background: linear-gradient(180deg, #eef7ff 0%, #f8fbff 34%, #ffffff 100%);
`;

const Hero = styled.section`
  border: 1px solid #cfe2f5;
  border-radius: 18px;
  padding: 16px;
  background: linear-gradient(130deg, #014f88, #0a77bf);
  color: #fff;
  box-shadow: 0 18px 40px rgba(1, 91, 154, 0.25);
`;

const HeroTitle = styled.h2`
  margin: 0;
  font-size: 23px;
  line-height: 1.2;
`;

const HeroSub = styled.p`
  margin: 8px 0 0;
  opacity: 0.92;
  font-size: 14px;
`;

const TopActions = styled.div`
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const PrimaryBtn = styled.button`
  border: 0;
  background: #fff;
  color: #015b9a;
  font-weight: 800;
  border-radius: 12px;
  padding: 10px 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const GhostBtn = styled.button`
  border: 1px solid rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  font-weight: 700;
  border-radius: 12px;
  padding: 10px 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const StatusPill = styled.div`
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 700;
  border-radius: 999px;
  padding: 8px 12px;
  background: ${({ $ok }) => ($ok ? "rgba(22, 163, 74, 0.18)" : "rgba(15, 23, 42, 0.26)")};
  border: 1px solid ${({ $ok }) => ($ok ? "rgba(22, 163, 74, 0.6)" : "rgba(255,255,255,0.28)")};
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #b42318;
  border-radius: 12px;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
`;

const Section = styled.section`
  margin-top: 14px;
  border: 1px solid #dce7f3;
  border-radius: 16px;
  background: #fff;
  padding: 14px;
`;

const SectionHead = styled.h3`
  margin: 0;
  color: #0f172a;
  font-size: 16px;
`;

const EmptyBox = styled.div`
  margin-top: 10px;
  border: 1px dashed #d3dfed;
  border-radius: 12px;
  color: #64748b;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
`;

const Grid = styled.div`
  margin-top: 10px;
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const Card = styled.article`
  border: 1px solid #d7e5f3;
  border-radius: 12px;
  padding: 12px;
  background: #fdfefe;
`;

const CardTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const DeviceName = styled.div`
  color: #0f172a;
  font-size: 15px;
  font-weight: 800;
  word-break: break-word;
`;

const ConnectedTag = styled.span`
  border-radius: 999px;
  padding: 5px 9px;
  border: 1px solid #bbf7d0;
  color: #166534;
  background: #f0fdf4;
  font-size: 11px;
  font-weight: 800;
`;

const MetaLine = styled.div`
  margin-top: 7px;
  color: #475569;
  font-size: 12px;
  line-height: 1.35;
`;

const RowActions = styled.div`
  margin-top: 10px;
`;

const CardBtn = styled.button`
  width: 100%;
  border: 1px solid #b8d7f0;
  background: #ecf6ff;
  color: #015b9a;
  border-radius: 10px;
  font-weight: 700;
  font-size: 13px;
  padding: 9px 10px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;
